import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Package, Star, Zap, CheckCircle, Gift, CreditCard, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CreditPackages = () => {
  const navigate = useNavigate();
  const [selectedPackage, setSelectedPackage] = useState('medium');

  const packages = [
    {
      id: 'starter',
      name: 'Starter Pack',
      credits: 10,
      price: 9,
      savings: 10,
      description: 'Perfect for occasional users',
      features: [
        '10 processing credits',
        'Process 1-2 small documents',
        'All analysis features included',
        'Schedule C export',
        'Excel/CSV downloads'
      ],
      popular: false,
      bestFor: 'Individuals who process documents once or twice a year'
    },
    {
      id: 'medium',
      name: 'Professional Pack',
      credits: 50,
      price: 40,
      savings: 20,
      description: 'Most popular for small businesses',
      features: [
        '50 processing credits',
        'Process 5-10 medium documents',
        'Priority processing',
        'All analysis features included',
        'Schedule C export',
        'Excel/CSV downloads',
        'Email support priority'
      ],
      popular: true,
      bestFor: 'Small businesses and frequent freelancers'
    },
    {
      id: 'business',
      name: 'Business Pack',
      credits: 150,
      price: 105,
      savings: 30,
      description: 'Best value for regular users',
      features: [
        '150 processing credits',
        'Process 15-30 large documents',
        'Priority processing',
        'All analysis features included',
        'Schedule C export',
        'Excel/CSV downloads',
        'Priority email & phone support',
        'Custom categorization rules'
      ],
      popular: false,
      bestFor: 'Growing businesses and accounting firms'
    },
    {
      id: 'enterprise',
      name: 'Enterprise Pack',
      credits: 500,
      price: 300,
      savings: 40,
      description: 'Maximum savings for heavy users',
      features: [
        '500 processing credits',
        'Process 50+ documents per month',
        'Highest priority processing',
        'All analysis features included',
        'Schedule C export',
        'Excel/CSV downloads',
        'Dedicated account manager',
        'Custom categorization rules',
        'API access (beta)',
        'Bulk processing tools'
      ],
      popular: false,
      bestFor: 'Large businesses and enterprise users'
    }
  ];

  const benefits = [
    {
      icon: Gift,
      title: 'Significant Savings',
      description: 'Save 10-40% compared to pay-per-use pricing'
    },
    {
      icon: Zap,
      title: 'Never Expires',
      description: 'Credits never expire - use them whenever you need'
    },
    {
      icon: Star,
      title: 'Priority Processing',
      description: 'Faster processing speeds for credit package users'
    },
    {
      icon: CheckCircle,
      title: 'No Commitment',
      description: 'One-time purchase, no recurring charges'
    }
  ];

  const calculateSavings = (pkg) => {
    const regularPrice = pkg.credits * 1; // $1 per credit
    const savings = regularPrice - pkg.price;
    const percentage = Math.round((savings / regularPrice) * 100);
    return { amount: savings, percentage };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Package className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Credit Packages
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Save money with credit packages. Buy in bulk and get more processing power for less.
          </p>
        </div>

        {/* Benefits */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-green-50 border-b">
            <CardTitle className="text-green-800">Why Choose Credit Packages?</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-slate-800 mb-2">{benefit.title}</h4>
                    <p className="text-slate-600 text-sm">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Package Selector */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {packages.map((pkg) => {
            const savings = calculateSavings(pkg);
            return (
              <Card 
                key={pkg.id} 
                className={`shadow-lg border-2 cursor-pointer transition-all hover:shadow-xl ${
                  pkg.popular 
                    ? 'border-blue-500 bg-blue-50 scale-105' 
                    : selectedPackage === pkg.id
                    ? 'border-blue-300 bg-blue-25'
                    : 'border-slate-200 hover:border-blue-200'
                }`}
                onClick={() => setSelectedPackage(pkg.id)}
              >
                {pkg.popular && (
                  <div className="bg-blue-600 text-white text-center py-2 rounded-t-lg">
                    <span className="text-sm font-semibold flex items-center justify-center gap-1">
                      <Star className="w-4 h-4" />
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className={`text-center ${pkg.popular ? 'pt-4' : ''}`}>
                  <CardTitle className="text-slate-800">{pkg.name}</CardTitle>
                  <div className="space-y-2">
                    <div className="text-3xl font-bold text-blue-600">${pkg.price}</div>
                    <div className="text-lg text-slate-600">{pkg.credits} credits</div>
                    <div className="text-sm text-green-600 font-medium">
                      Save ${savings.amount} ({savings.percentage}%)
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-slate-600 text-sm mb-4">{pkg.description}</p>
                  <div className="space-y-2 text-left">
                    {pkg.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6">
                    <Button 
                      className={`w-full ${
                        pkg.popular 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-slate-600 hover:bg-slate-700 text-white'
                      }`}
                      onClick={() => navigate('/auth')}
                    >
                      Choose Package
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Package Details */}
        {selectedPackage && (
          <Card className="shadow-xl border-2 border-blue-200 bg-blue-50 mb-12">
            <CardHeader className="bg-blue-100 border-b">
              <CardTitle className="text-blue-800">
                {packages.find(p => p.id === selectedPackage)?.name} Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">Perfect For:</h4>
                  <p className="text-blue-700 mb-4">
                    {packages.find(p => p.id === selectedPackage)?.bestFor}
                  </p>
                  
                  <h4 className="font-semibold text-blue-800 mb-3">How Credits Work:</h4>
                  <div className="space-y-2 text-blue-700 text-sm">
                    <p>• Each page processed uses 1 credit</p>
                    <p>• Upload fee is waived for credit package users</p>
                    <p>• Credits never expire</p>
                    <p>• Can be used for any document type</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-blue-800 mb-3">Usage Examples:</h4>
                  <div className="space-y-3 text-sm">
                    {selectedPackage === 'starter' && (
                      <>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">2-page statement × 5 times</p>
                          <p className="text-slate-600">Perfect for quarterly processing</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">10-page annual summary</p>
                          <p className="text-slate-600">Complete year-end analysis</p>
                        </div>
                      </>
                    )}
                    {selectedPackage === 'medium' && (
                      <>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">5-page statement × 10 times</p>
                          <p className="text-slate-600">Monthly processing for small business</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">Mix of 2-20 page documents</p>
                          <p className="text-slate-600">Flexible for varying needs</p>
                        </div>
                      </>
                    )}
                    {selectedPackage === 'business' && (
                      <>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">15-page statement × 10 times</p>
                          <p className="text-slate-600">Growing business with multiple accounts</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">Multiple client documents</p>
                          <p className="text-slate-600">Perfect for bookkeepers and accountants</p>
                        </div>
                      </>
                    )}
                    {selectedPackage === 'enterprise' && (
                      <>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">100+ pages per month</p>
                          <p className="text-slate-600">Large-scale document processing</p>
                        </div>
                        <div className="bg-white p-3 rounded-lg">
                          <p className="font-medium text-slate-800">Multiple locations/entities</p>
                          <p className="text-slate-600">Enterprise-level tax preparation</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparison Table */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-slate-50 border-b">
            <CardTitle className="text-slate-800">Package Comparison</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-800">Feature</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Pay-Per-Use</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Starter</th>
                    <th className="text-center py-3 px-4 font-semibold text-blue-800">Professional</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Business</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-800">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Cost per Credit</td>
                    <td className="py-3 px-4 text-center">$1.00</td>
                    <td className="py-3 px-4 text-center">$0.90</td>
                    <td className="py-3 px-4 text-center text-blue-600 font-semibold">$0.80</td>
                    <td className="py-3 px-4 text-center">$0.70</td>
                    <td className="py-3 px-4 text-center">$0.60</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Upload Fees</td>
                    <td className="py-3 px-4 text-center text-red-600">$1 per file</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Waived</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Waived</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Waived</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Waived</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Priority Processing</td>
                    <td className="py-3 px-4 text-center">Standard</td>
                    <td className="py-3 px-4 text-center">Standard</td>
                    <td className="py-3 px-4 text-center text-blue-600">✓ Priority</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Priority</td>
                    <td className="py-3 px-4 text-center text-green-600">✓ Highest</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-700">Support Level</td>
                    <td className="py-3 px-4 text-center">Email</td>
                    <td className="py-3 px-4 text-center">Email</td>
                    <td className="py-3 px-4 text-center text-blue-600">Priority Email</td>
                    <td className="py-3 px-4 text-center text-green-600">Email + Phone</td>
                    <td className="py-3 px-4 text-center text-green-600">Dedicated Manager</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-slate-700">Credit Expiration</td>
                    <td className="py-3 px-4 text-center">N/A</td>
                    <td className="py-3 px-4 text-center text-green-600">Never</td>
                    <td className="py-3 px-4 text-center text-green-600">Never</td>
                    <td className="py-3 px-4 text-center text-green-600">Never</td>
                    <td className="py-3 px-4 text-center text-green-600">Never</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* FAQ */}
        <Card className="shadow-lg border-0 mb-12">
          <CardHeader className="bg-yellow-50 border-b">
            <CardTitle className="text-yellow-800">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Do credits expire?</h4>
                <p className="text-slate-700">No! Credits never expire. Buy them when you need them and use them whenever convenient.</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Can I mix pay-per-use and credit packages?</h4>
                <p className="text-slate-700">Absolutely! If you run out of credits, you can always process additional documents with pay-per-use pricing.</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">What happens if I don't use all my credits?</h4>
                <p className="text-slate-700">No problem! Credits never expire, so you can use them next tax season or whenever you need document processing.</p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Can I upgrade my package later?</h4>
                <p className="text-slate-700">Yes! You can purchase additional credit packages at any time. Your existing credits will remain available.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="shadow-xl border-2 border-green-200 bg-green-50">
          <CardContent className="p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <CreditCard className="w-8 h-8 text-green-600" />
              <h3 className="text-2xl font-bold text-green-800">Ready to Save with Credit Packages?</h3>
            </div>
            <p className="text-green-700 text-lg mb-6">
              Choose the package that fits your needs. No commitments, no monthly fees, just better value 
              for your document processing.
            </p>
            <div className="flex justify-center gap-4">
              <Button 
                onClick={() => navigate('/auth')}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/pay-per-use')}
                className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3"
              >
                Compare with Pay-Per-Use
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/bulk-discounts')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            View Bulk Discounts
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreditPackages;